/**
 * WebPWAAdapter — v10.12.0
 * Adaptador XR para navegador web (PWA móvil/tablet).
 *
 * Cambios v10.12.0:
 *  - Solicita la cámara TRASERA por defecto (`facingMode: environment`)
 *    con resolución alta (1920x1080 ideal) para calidad de detección.
 *  - Reporta al callback `onMarker` un `bounds` NORMALIZADO {x,y,w,h,cx,cy}
 *    donde 0..1 son coordenadas del cuadro de video visible. El overlay
 *    3D usa este bounds para escalar el modelo al tamaño real del marker
 *    (fantoma cerca → overlay grande; lejos → pequeño).
 *  - Emite `onLost(marker_id)` cuando un marker deja de detectarse por
 *    600 ms (útil para ocultar el overlay).
 *  - `latestFrame()` público — retorna { videoWidth, videoHeight, ratio }
 *    para que el overlay pueda compensar `object-fit: cover`.
 */
import { BrowserMultiFormatReader } from '@zxing/browser';
import { XRDeviceAdapter } from './XRDeviceAdapter';

const LOST_TIMEOUT_MS = 600;

export class WebPWAAdapter extends XRDeviceAdapter {
  static get id() { return 'web_pwa'; }

  constructor(opts = {}) {
    super({ deviceType: opts.deviceType || 'mobile_ar', deviceName: opts.deviceName });
    this._reader = null;
    this._videoEl = null;
    this._detectedBuckets = new Set();
    this._activeMarkers = new Map(); // marker_id -> { bounds, lastSeenAt, timer }
    this._handlers = null;
  }

  async capabilities() {
    const caps = {
      camera_supported: false,
      marker_tracking_supported: false,
      webxr_supported: false,
      depth_supported: false,
      spatial_anchor_supported: false,
      hand_tracking_supported: false,
      voice_supported: false,
    };
    try {
      if (navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function') {
        caps.camera_supported = true;
        caps.marker_tracking_supported = true;
      }
      if (typeof navigator.xr !== 'undefined' && navigator.xr && typeof navigator.xr.isSessionSupported === 'function') {
        try { caps.webxr_supported = !!(await navigator.xr.isSessionSupported('immersive-ar')); } catch { /* noop */ }
      }
      caps.voice_supported = 'speechSynthesis' in window || 'webkitSpeechRecognition' in window;
    } catch { /* noop */ }
    return caps;
  }

  attachVideo(el) { this._videoEl = el; }
  getVideoElement() { return this._videoEl; }

  latestFrame() {
    const v = this._videoEl;
    if (!v || !v.videoWidth) return { videoWidth: 0, videoHeight: 0, ratio: 1 };
    return { videoWidth: v.videoWidth, videoHeight: v.videoHeight, ratio: v.videoWidth / v.videoHeight };
  }

  _boundsFromResultPoints(result) {
    // zxing devuelve `resultPoints` (típicamente 3-4 para QR: patrones de
    // esquina). Calculamos el bbox y normalizamos por el tamaño del video.
    const pts = (result && typeof result.getResultPoints === 'function' ? result.getResultPoints() : null) || [];
    if (!pts.length || !this._videoEl?.videoWidth) return null;
    const vw = this._videoEl.videoWidth;
    const vh = this._videoEl.videoHeight;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of pts) {
      const x = p.getX ? p.getX() : p.x;
      const y = p.getY ? p.getY() : p.y;
      if (typeof x !== 'number' || typeof y !== 'number') continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    if (!isFinite(minX) || !isFinite(maxX)) return null;
    // Los QR tienen 3 patrones grandes en las esquinas — inflamos el bbox
    // un 25% para aproximar el cuadrado completo del código.
    const rawW = maxX - minX;
    const rawH = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const w = Math.max(rawW, rawH) * 1.25;
    const nx = (cx - w / 2) / vw;
    const ny = (cy - w / 2) / vh;
    const nw = w / vw;
    const nh = w / vh;
    return {
      x: Math.max(0, Math.min(1, nx)),
      y: Math.max(0, Math.min(1, ny)),
      w: Math.max(0, Math.min(1, nw)),
      h: Math.max(0, Math.min(1, nh)),
      cx: cx / vw,
      cy: cy / vh,
    };
  }

  _touchLost(markerId) {
    const rec = this._activeMarkers.get(markerId);
    if (rec?.timer) clearTimeout(rec.timer);
    const timer = setTimeout(() => {
      this._activeMarkers.delete(markerId);
      try { this._handlers?.onLost && this._handlers.onLost({ marker_id: markerId }); } catch { /* noop */ }
    }, LOST_TIMEOUT_MS);
    this._activeMarkers.set(markerId, { ...(rec || {}), timer, lastSeenAt: Date.now() });
  }

  async startMarkerTracking(handlers = {}) {
    if (!this._videoEl) throw new Error('videoEl no adjunto — llamar attachVideo() primero');
    this._handlers = handlers;
    try {
      // Prioriza cámara trasera + alta resolución.
      const constraints = {
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this._videoEl.srcObject = stream;
      await this._videoEl.play().catch(() => { /* iOS puede requerir gesto */ });

      this._reader = new BrowserMultiFormatReader();
      await this._reader.decodeFromVideoElement(this._videoEl, (result, err) => {
        if (!result) return;
        const raw = String(result.getText() || '').trim();
        if (!raw) return;
        const markerId = raw.replace(/^xr:/i, '');
        const bounds = this._boundsFromResultPoints(result);

        // Live update (posición sigue al marker con dedupe suave a 15fps).
        try { handlers.onFrame && handlers.onFrame({ marker_id: markerId, bounds }); } catch { /* noop */ }
        this._touchLost(markerId);

        // Detección "hard" (dispara evento clínico) — dedup 3s.
        const bucket = `${markerId}@${Math.floor(Date.now() / 3000)}`;
        if (this._detectedBuckets.has(bucket)) return;
        this._detectedBuckets.add(bucket);
        try {
          handlers.onMarker && handlers.onMarker({
            raw, marker_id: markerId, bounds, adapter: 'web_pwa',
            at: new Date().toISOString(),
          });
        } catch (e) { handlers.onError && handlers.onError(e); }
      });
    } catch (e) {
      handlers.onError && handlers.onError(e);
      throw e;
    }
  }

  async stopMarkerTracking() {
    try {
      if (this._reader && typeof this._reader.reset === 'function') this._reader.reset();
      const stream = this._videoEl?.srcObject;
      if (stream && stream.getTracks) stream.getTracks().forEach((t) => t.stop());
      if (this._videoEl) this._videoEl.srcObject = null;
      for (const rec of this._activeMarkers.values()) if (rec.timer) clearTimeout(rec.timer);
    } catch { /* noop */ }
    this._reader = null;
    this._detectedBuckets.clear();
    this._activeMarkers.clear();
    this._handlers = null;
  }
}
